import { useTranslation } from "react-i18next";
import { Box } from "@caspeco/casper-ui-library.components.box";
import { ThemeSpaceVariable } from "@caspeco/casper-ui-library.base-ui.theme";
import { Alert } from "@caspeco/casper-ui-library.components.alert";
import { Section } from "types/section";
import { getArticleQuantity } from "helpers/cart-helper/cart-helper";
import { useCartStore } from "store/cartStore";
import { usePlaceStore } from "store/placeStore";
import { useMenuStore } from "store/menuStore";
import { getMenuSectionAlert } from "helpers/menu-section-opening-hours-helper/menu-section-opening-hours-helper";
import { usePickSearchParams } from "hooks/usePickSearchParams";
import ArticleCard from "components/article-card/ArticleCard";
import NotFound from "components/not-found/NotFound";
import { StyledArticleGrid } from "./MenuSection.styles";

interface IMenuSection {
	section: Section;
	onClickArticle: (articleId: string, articleName: string) => void;
}

function MenuSection({ onClickArticle, section }: IMenuSection) {
	const { menu, hasLoadedMenu } = useMenuStore();
	const { deliveryOptionType } = usePickSearchParams(["deliveryOptionType"]);
	const { place } = usePlaceStore();
	const { cart } = useCartStore();
	const { t } = useTranslation();

	const articles = section?.articles ?? [];

	if (hasLoadedMenu && !section) {
		return (
			<Box display="flex" justifyContent="center" w="100%" mt={ThemeSpaceVariable.XLarge}>
				<NotFound title={t("error_get_menu")} />
			</Box>
		);
	}

	const alert = getMenuSectionAlert(place, menu, section, deliveryOptionType);

	return (
		<Box px={ThemeSpaceVariable.Medium} pb={ThemeSpaceVariable.Medium}>
			{articles.length > 0 && alert && (
				<Box pt={ThemeSpaceVariable.Medium}>
					<Alert
						pl={ThemeSpaceVariable.Medium}
						pr={ThemeSpaceVariable.Small}
						type={alert.type}
						icon={alert.icon}
						iconVerticalAlign="flex-start"
					>
						{alert.description}
					</Alert>
				</Box>
			)}
			{articles.length > 0 ? (
				<StyledArticleGrid>
					{articles.map((article) => {
						const quantity = getArticleQuantity(cart, article.id);
						return (
							<ArticleCard key={article.id} article={article} onClick={onClickArticle} quantity={quantity} />
						);
					})}
				</StyledArticleGrid>
			) : (
				<NotFound
					title={t("menu_section_articles_empty_title")}
					description={t("menu_section_x_articles_empty_description", {
						0: section.name,
					})}
				/>
			)}
		</Box>
	);
}

export default MenuSection;
